import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import type { Household } from "../../../../api/src/households-base/households-base.service";
import type { HouseholdsController } from "../../../../api/src/households/households.controller";
import type { UnpackResponse } from "../../../../api/src/lib";
import type {
  DataMapping,
  MappingData,
  TargetMappingData,
} from "../../../../api/src/lib/dataMapping";
import DataMappingModal from "../../components/DataMapping";
import { useErrorMessage } from "../../components/FormError";
import {
  useAuthenticatedFetch,
  refusalSentence,
  useAuthenticatedMutationAsync,
} from "../../lib/api";

type LinkData = Record<number, number[]>;

const AccountsHouseholdsMapping = () => {
  const { errorMessage, setError, resetError } = useErrorMessage();

  const {
    isPending: isPendingMapping,
    isError: isErrorMapping,
    data: householdAccountMappings,
  } = useAuthenticatedFetch<
    UnpackResponse<HouseholdsController["getAccountHouseholdMappings"]>
  >("/v1/households/accounts/mappings");

  const {
    isFetching: isFetchingSuggestions,
    data: suggestions,
    refetch: fetchSuggestions,
  } = useAuthenticatedFetch<
    UnpackResponse<
      HouseholdsController["getAccountHouseholdMappingSuggestions"]
    >
  >("/v1/households/accounts/mappings/suggestions", undefined, {
    enabled: false,
  });

  const onAutoSuggest = useCallback(async () => {
    resetError();

    const { isError, error } = await fetchSuggestions();
    if (isError) {
      const message = "Failed to auto-match households";
      setError(message);
      console.error(message, error);
    }
  }, [fetchSuggestions, setError, resetError]);

  const linkAccountHouseholds = useAuthenticatedMutationAsync<
    UnpackResponse<HouseholdsController["mapAccountsToHouseholds"]>
  >("/v1/households/accounts/mappings", async (data: LinkData) => ({
    method: "POST",
    body: JSON.stringify(data),
  }));

  const queryClient = useQueryClient();

  const { mutateAsync: onLink, isPending: isPendingOnLink } = useMutation({
    mutationFn: async (data: LinkData) => {
      resetError();

      try {
        const linkedAccountHouseholds = await linkAccountHouseholds(data);
        // Remove the held Auto-Match suggestions after a save that succeeds
        // (APP-2036). The mapping table adds each suggestion to its row again
        // when the mapping data changes, so a saved account would show two
        // times in one household. A failed save does not reach this line and
        // keeps the suggestions. The query is not enabled, so the reset sends
        // no read; a click on Auto-Match does.
        await queryClient.resetQueries({
          queryKey: ["/v1/households/accounts/mappings/suggestions"],
        });
        queryClient.setQueryData(["/v1/households/accounts/mappings"], {
          data: linkedAccountHouseholds.data,
        });
      } catch (err) {
        // The API `message` of a refusal from 400 to 499, else the fixed text
        // (APP-2037).
        const message = "Failed to assign accounts to households";
        setError(refusalSentence(err) ?? message);
        console.error(message, err);
      }
    },
  });

  const handleSyncHouseholds = useCallback(
    async (newAssignedData: TargetMappingData<Household>[]) => {
      await onLink(
        newAssignedData.reduce(
          (result, household) => ({
            ...result,
            [household.id]: household.assigned.accounts.map((assignedItem) =>
              Number(assignedItem.id),
            ),
          }),
          {} as LinkData,
        ),
      );
    },
    [onLink],
  );

  const mappingData = useMemo(
    // An account under `source` has no `tags` (`AccountMappingData`,
    // `api/src/households/households.service.ts`).
    (): DataMapping<Household, Pick<MappingData, "id" | "name">> => ({
      ...(householdAccountMappings?.data ?? {
        target: [],
        source: {},
      }),
      // `GET /v1/households/accounts/mappings/suggestions` answers
      // `{ suggested: [{ householdId, accountIds }] }` (APP-1653); the
      // mapping table reads a map of household ID to account IDs, as before.
      suggested: Object.fromEntries(
        (suggestions?.data.suggested ?? []).map(
          ({ householdId, accountIds }) => [householdId, accountIds],
        ),
      ),
    }),
    [householdAccountMappings, suggestions],
  );

  return (
    <>
      {errorMessage}
      <DataMappingModal
        linkHeader="/clients/households/"
        title="Assign Accounts to Households"
        targetTitle="Households"
        mappedTitle="Accounts"
        sourceTitle="Unassigned Accounts"
        isLoading={isPendingMapping}
        isError={isErrorMapping}
        mappingData={mappingData}
        isSubmitting={isPendingOnLink}
        handleSubmit={handleSyncHouseholds}
        onAutoSuggest={onAutoSuggest}
        isLoadingSuggestions={isFetchingSuggestions}
      />
    </>
  );
};

export default AccountsHouseholdsMapping;
